import { mapState } from 'pinia'
import { defineAsyncComponent, h } from 'vue'

import { REPLY_PREVIEW_OFFSET } from 'src/components/mention_link/mention_link.js'
import MentionLink from 'src/components/mention_link/mention_link.vue'
import MentionPill from 'src/components/mention_pill/mention_pill.vue'
import Popover from 'src/components/popover/popover.vue'

import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useUsersStore } from 'src/stores/users.js'

import { displayName } from 'src/services/user_display/user_display.js'

// shows a post, which has rich content: no import cycle
const StatusPopover = defineAsyncComponent(
  () => import('src/components/status_popover/status_popover.vue'),
)

export const MENTIONS_LIMIT = 5
// faces shown in the closed pill of leading mentions
const FACES = 5

// The pill for several people: a MentionPill of their faces and how many.
export const LeadingStack = {
  // reply: { statusId } when the post replies to the first of them; the
  // pill then starts with the reply sign. preview: the copy in the opened
  // overlay, which shows the replied-to post on hover and goes there on a
  // click (the named mentions beside it show who is mentioned)
  props: ['users', 'count', 'reply', 'preview'],
  inject: {
    gotoReplyParent: { default: null },
  },
  render() {
    const stack = (attrs = {}) =>
      h(
        MentionPill,
        {
          class: 'leading-stack',
          users: this.users,
          reply: this.reply,
          ...attrs,
        },
        {
          default: () =>
            h('span', { class: 'mention-pill-count' }, String(this.count)),
        },
      )
    if (!(this.preview && this.reply?.statusId && !this.reply.plain)) {
      return stack()
    }
    // the opened copy: the whole stack previews the replied-to post
    return h(
      StatusPopover,
      {
        statusId: this.reply.statusId,
        class: 'leading-reply-popover',
        offset: REPLY_PREVIEW_OFFSET,
        anchorSelector: '.leading-overlay',
      },
      {
        default: () =>
          stack({
            onClick: (event) => {
              event.stopPropagation()
              this.gotoReplyParent?.()
            },
          }),
      },
    )
  },
}

const MentionsLine = {
  name: 'MentionsLine',
  props: {
    mentions: {
      required: true,
      type: Array,
    },
    // the mentions a post starts with: several known people are one pill
    // of faces; it opens over the post, a copy of the pill over the pill
    // with the named mentions after it, so nothing under it moves
    leading: Boolean,
    // the post replies to the first of these: its pill, or the stack of
    // faces, carries the reply sign (see MentionLink)
    replyTo: { type: Object, default: null },
  },
  data: () => ({ expanded: false }),
  created() {
    // the opened stack of a reply is a post popover: load it now, so the
    // overlay has the stack copy to place over the pill on the first hover
    if (this.replyTo?.statusId) StatusPopover.__asyncLoader()
  },
  components: {
    LeadingStack,
    MentionLink,
    Popover,
  },
  computed: {
    mentionsComputed() {
      return this.mentions.slice(0, MENTIONS_LIMIT)
    },
    extraMentions() {
      return this.mentions.slice(MENTIONS_LIMIT)
    },
    manyMentions() {
      return this.extraMentions.length > 0
    },
    users() {
      const store = useUsersStore()
      return this.mentions.map((m) => m.url && store.findUserByUrl(m.url))
    },
    collapsed() {
      return (
        this.leading && this.mentions.length > 1 && this.users.every(Boolean)
      )
    },
    faceUsers() {
      return this.users.slice(0, FACES)
    },
    // keyboard: focusing the pill opens it, leaving it closes it
    triggerAttrs() {
      const names = this.users.map(displayName).join(', ')
      return {
        'aria-label': this.$t('status.mentioned', { names }),
        onFocus: () => this.$refs.popover?.showPopover(),
        onBlur: () => this.$refs.popover?.hidePopover(),
      }
    },
    ...mapState(useMergedConfigStore, ['mergedConfig']),
  },
  methods: {
    toggleShowMore() {
      this.expanded = !this.expanded
    },
  },
}

export default MentionsLine
